
/**
 * Options for the hover intent class.
 */
export interface HoverIntentOptions {
    sensitivity?: number;  // sensitivity threshold (must be 1 or higher)
    interval?: number;     // milliseconds for onMouseOver polling interval
    timeout?: number;      // milliseconds delay before onMouseOut
}

// Used by the mega menu to make navigation easier so that the mouse in / out events
// are delayed. This helps when the user tracks the mouse from the menu parent to its children
// and it goes over the parent menus siblings. If this wasn't in place, the parent menu would
// disappear before the user got to the child elements.
//
// Usage with defaults;
//      const hover = new Outdoor.Pages.HoverIntent(element, onOverCallback, onOutCallback);
// Usage with custom values applied over defaults;
//      const hover = new Outdoor.Pages.HoverIntent(element, onOverCallback, onOutCallback)
//          .configure({ sensitivity: 10, interval: 150 });
export class HoverIntent {
    private x: number = 0;
    private y: number = 0;
    private pX: number = 0;
    private pY: number = 0;
    private state: number = 0;
    private timer: number | null = null;

    // Default options.
    private options: HoverIntentOptions = {
        sensitivity: 3,
        interval: 100,
        timeout: 500,
    };

    constructor(private el: HTMLElement, private onOver: (e: MouseEvent) => void, private onOut: (e: MouseEvent) => void) {
        if (this.el) {
            this.el.addEventListener('mouseover', (e) => this.dispatchOver(e));
            this.el.addEventListener('mouseout', (e) => this.dispatchOut(e));
        }
    }

    private delay(e: MouseEvent): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.state = 0;
        this.onOut.call(this.el, e);
    }

    private tracker(e: MouseEvent): void {
        this.x = e.clientX;
        this.y = e.clientY;
    }

    private compare(e: MouseEvent): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if ((Math.abs(this.pX - this.x) + Math.abs(this.pY - this.y)) < this.options.sensitivity) {
            this.state = 1;
            this.onOver.call(this.el, e);
        } else {
            this.pX = this.x;
            this.pY = this.y;
            this.timer = window.setTimeout(() => this.compare(e), this.options.interval);
        }
    }

    public configure(opt: Partial<HoverIntentOptions>): HoverIntent {
        Object.assign(this.options, opt);
        return this;
    }

    private dispatchOver(e: MouseEvent): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }

        this.el.removeEventListener('mousemove', this.tracker.bind(this));

        if (this.state !== 1) {
            this.pX = e.clientX;
            this.pY = e.clientY;

            this.el.addEventListener('mousemove', this.tracker.bind(this));

            this.timer = window.setTimeout(() => this.compare(e), this.options.interval);
        }
    }

    private dispatchOut(e: MouseEvent): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.el.removeEventListener('mousemove', this.tracker.bind(this));

        if (this.state === 1) {
            this.timer = window.setTimeout(() => this.delay(e), this.options.timeout);
        }
    }

    public remove(): void {
        if (!this.el) {
            return;
        }
        this.el.removeEventListener('mouseover', (e) => this.dispatchOver(e));
        this.el.removeEventListener('mouseout', (e) => this.dispatchOut(e));
    }
}
